import type { Locale } from '@platform/i18n';
import type { ShippingClient, TypedClient } from '@platform/openapi';
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  Outlet,
  redirect,
  stripSearchParams,
  type RouterHistory,
} from '@tanstack/react-router';
import { CalculatorPage } from '../features/calculator/CalculatorPage.tsx';
import { STATISTICS_ROLE } from '../features/statistics/statistics.ts';
import { StatisticsRoute } from './StatisticsRoute.tsx';
import { CategoriesPage } from '../features/catalog/CategoriesPage.tsx';
import { ProductsPage } from '../features/catalog/ProductsPage.tsx';
import { ProductEditorRoute } from './ProductEditorRoute.tsx';
import { PRODUCTS_DEFAULTS, validateProductsSearch, type ProductsSearch } from './products-search.ts';
import { InventoryPage } from '../features/inventory/InventoryPage.tsx';
import type { InventoryTab } from '../features/inventory/inventory.ts';
import { CustomersPage } from '../features/customers/CustomersPage.tsx';
import { EditOrderPage } from '../features/orders/EditOrderPage.tsx';
import { NewOrderPage } from '../features/orders/NewOrderPage.tsx';
import { OrdersPage } from '../features/orders/OrdersPage.tsx';
import { CallbackPage } from '../pages/CallbackPage.tsx';
import { GuardedDashboard } from './GuardedDashboard.tsx';
import { ForbiddenPage } from '../pages/ForbiddenPage.tsx';
import { LoginPage } from '../pages/LoginPage.tsx';
import { PlaceholderPage } from '../pages/PlaceholderPage.tsx';
import { AUDIT_READ_ROLE } from '../features/history/history.ts';
import { HistoryPage } from '../features/history/HistoryPage.tsx';
import { LabelsPage } from '../features/labels/LabelsPage.tsx';
import { NotificationsPage } from '../features/notifications/NotificationsPage.tsx';
import { SETTINGS_MANAGE_ROLE } from '../features/settings/order-settings.ts';
import { SettingsPage } from '../features/settings/SettingsPage.tsx';
import { SetupRoute } from './SetupRoute.tsx';
import { HandoverPage } from '../features/handover/HandoverPage.tsx';
import { ReturnsPage } from '../features/returns/ReturnsPage.tsx';
import { MissingParcelsPage } from '../features/parcels/MissingParcelsPage.tsx';
import { ReturnedParcelsPage } from '../features/parcels/ReturnedParcelsPage.tsx';
import { DisputesPage } from '../features/finance/DisputesPage.tsx';
import { ExpensesPage } from '../features/finance/ExpensesPage.tsx';
import { FinanceOverviewPage } from '../features/finance/FinanceOverviewPage.tsx';
import type { FinanceSection } from '../features/finance/FinanceSections.tsx';
import { RemittancePage } from '../features/finance/RemittancePage.tsx';
import { RemittancesPage } from '../features/finance/RemittancesPage.tsx';
import { useOpenLabels } from './useOpenLabels.ts';
import { useOpenHistorySubject } from './useOpenHistorySubject.ts';
import { AuthenticatedShell } from '../shell/AuthenticatedShell.tsx';
import { SUPER_ADMIN_ROLE, type RouterContext } from './context.ts';
import { localeRewrite } from './locale-path.ts';
import {
  CATEGORIES_DEFAULTS,
  DASHBOARD_DEFAULTS,
  EXPENSES_DEFAULTS,
  INVENTORY_DEFAULTS,
  ORDERS_DEFAULTS,
  HANDOVER_DEFAULTS,
  RETURNS_DEFAULTS,
  MISSING_PARCELS_DEFAULTS,
  RETURNED_PARCELS_DEFAULTS,
  CUSTOMERS_DEFAULTS,
  HISTORY_DEFAULTS,
  LABELS_DEFAULTS,
  NOTIFICATIONS_DEFAULTS,
  SETUP_DEFAULTS,
  STATISTICS_DEFAULTS,
  validateLoginSearch,
  validateCategoriesSearch,
  validateDashboardSearch,
  validateExpensesSearch,
  validateInventorySearch,
  validateOrdersSearch,
  validateHandoverSearch,
  validateReturnsSearch,
  validateMissingParcelsSearch,
  validateReturnedParcelsSearch,
  validateCustomersSearch,
  validateHistorySearch,
  validateLabelsSearch,
  validateNotificationsSearch,
  validateSetupSearch,
  validateStatisticsSearch,
  type LoginSearch,
  type CategoriesSearch,
  type DashboardSearch,
  type ExpensesSearch,
  type InventorySearch,
  type OrdersSearch,
  type HandoverSearch,
  type ReturnsSearch,
  type MissingParcelsSearch,
  type ReturnedParcelsSearch,
  type CustomersSearch,
  type HistorySearch,
  type LabelsSearch,
  type NotificationsSearch,
  type SetupSearch,
  type StatisticsSearch,
} from './search-params.ts';

const requireSignIn = ({ context, location }: { context: RouterContext; location: { href: string } }) => {
  if (context.auth.status === 'anonymous')
    throw redirect({ to: '/login', search: { returnTo: location.href } });
};

const requireRole =
  (role: string) =>
  ({ context }: { context: RouterContext }) => {
    if (context.auth.status === 'authenticated' && !context.auth.hasRole(role))
      throw redirect({ to: '/forbidden' });
  };

export type ApiClients = { readonly api: TypedClient; readonly shipping: ShippingClient };

export const buildRouteTree = ({ api, shipping }: ApiClients) => {
  const rootRoute = createRootRouteWithContext<RouterContext>()({ component: Outlet });

  const loginRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/login',
    validateSearch: validateLoginSearch,
    beforeLoad: ({ context, search }) => {
      if (context.auth.status === 'authenticated') throw redirect({ to: search.returnTo ?? '/' });
    },
    component: function LoginRoute() {
      const search: LoginSearch = loginRoute.useSearch();
      return <LoginPage returnTo={search.returnTo ?? '/'} />;
    },
  });

  const callbackRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/auth/callback',
    component: CallbackPage,
  });
  const forbiddenRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/forbidden',
    component: ForbiddenPage,
  });

  const authenticatedRoute = createRoute({
    getParentRoute: () => rootRoute,
    id: 'authenticated',
    beforeLoad: requireSignIn,
    component: function AuthenticatedRoute() {
      return <AuthenticatedShell api={api} />;
    },
  });

  const dashboardRoute = createRoute({
    getParentRoute: () => authenticatedRoute,
    path: '/',
    validateSearch: validateDashboardSearch,
    search: { middlewares: [stripSearchParams(DASHBOARD_DEFAULTS)] },
    component: function DashboardRoute() {
      const search: DashboardSearch = dashboardRoute.useSearch();
      const navigate = dashboardRoute.useNavigate();
      return (
        <GuardedDashboard
          api={api}
          shipping={shipping}
          company={search.company}
          productFilter={{ productId: search.product, origin: search.origin }}
          days={{ from: search.from, to: search.to }}
          comparison={search.compare}
          viewId={search.view}
          onCompanyChange={(company) => {
            void navigate({ search: { ...search, company } });
          }}
          onProductFilterChange={({ productId, origin }) => {
            void navigate({ search: { ...search, product: productId, origin } });
          }}
          onDaysChange={(days) => {
            void navigate({ search: { ...search, ...days } });
          }}
          onComparisonChange={(compare) => {
            void navigate({ search: { ...search, compare } });
          }}
          onViewChange={(view) => {
            void navigate({ search: { ...search, view } });
          }}
        />
      );
    },
  });
  const ordersRoute = createRoute({
    getParentRoute: () => authenticatedRoute,
    path: '/orders',
    validateSearch: validateOrdersSearch,
    search: { middlewares: [stripSearchParams(ORDERS_DEFAULTS)] },
    component: function OrdersRoute() {
      const search: OrdersSearch = ordersRoute.useSearch();
      const navigate = ordersRoute.useNavigate();
      return (
        <OrdersPage
          api={api}
          shipping={shipping}
          view={search.view}
          filters={{
            carrier: search.carrier,
            governorate: search.governorate,
            company: search.company,
            period: search.period,
          }}
          openOrderId={search.order}
          onViewChange={(view) => {
            void navigate({ search: { ...search, view, order: null } });
          }}
          onFiltersChange={(filters) => {
            void navigate({ search: { ...search, ...filters, order: null } });
          }}
          onOpenOrder={(order) => {
            void navigate({ search: { ...search, order } });
          }}
          onCreate={() => {
            void navigate({ to: '/orders/new' });
          }}
          onEdit={(orderId) => {
            void navigate({ to: '/orders/$orderId/edit', params: { orderId } });
          }}
          onOpenSettings={() => {
            void navigate({ to: '/settings' });
          }}
        />
      );
    },
  });
  const newOrderRoute = createRoute({
    getParentRoute: () => authenticatedRoute,
    path: '/orders/new',
    component: function NewOrderRoute() {
      const navigate = newOrderRoute.useNavigate();
      return (
        <NewOrderPage
          api={api}
          onCreated={(order) => {
            void navigate({ to: '/orders', search: { ...ORDERS_DEFAULTS, order } });
          }}
        />
      );
    },
  });
  const editOrderRoute = createRoute({
    getParentRoute: () => authenticatedRoute,
    path: '/orders/$orderId/edit',
    component: function EditOrderRoute() {
      const { orderId } = editOrderRoute.useParams();
      const navigate = editOrderRoute.useNavigate();
      return (
        <EditOrderPage
          api={api}
          shipping={shipping}
          orderId={orderId}
          onDone={(order) => {
            void navigate({ to: '/orders', search: { ...ORDERS_DEFAULTS, order } });
          }}
        />
      );
    },
  });
  const financePaths: Readonly<
    Record<FinanceSection, '/finance' | '/finance/remittances' | '/finance/disputes' | '/finance/expenses'>
  > = {
    overview: '/finance',
    remittances: '/finance/remittances',
    disputes: '/finance/disputes',
    expenses: '/finance/expenses',
  };
  const financeRoute = createRoute({
    getParentRoute: () => authenticatedRoute,
    path: '/finance',
    component: function FinanceRoute() {
      const navigate = financeRoute.useNavigate();
      return (
        <FinanceOverviewPage
          api={api}
          shipping={shipping}
          onSection={(section) => {
            void navigate({ to: financePaths[section] });
          }}
        />
      );
    },
  });
  const disputesRoute = createRoute({
    getParentRoute: () => authenticatedRoute,
    path: '/finance/disputes',
    component: function DisputesRoute() {
      const navigate = disputesRoute.useNavigate();
      return (
        <DisputesPage
          api={api}
          shipping={shipping}
          onSection={(section) => {
            void navigate({ to: financePaths[section] });
          }}
        />
      );
    },
  });
  const expensesRoute = createRoute({
    getParentRoute: () => authenticatedRoute,
    path: '/finance/expenses',
    validateSearch: validateExpensesSearch,
    search: { middlewares: [stripSearchParams(EXPENSES_DEFAULTS)] },
    component: function ExpensesRoute() {
      const search: ExpensesSearch = expensesRoute.useSearch();
      const navigate = expensesRoute.useNavigate();
      return (
        <ExpensesPage
          api={api}
          view={search.view}
          company={search.company}
          onViewChange={(view) => {
            void navigate({ search: { ...search, view } });
          }}
          onCompanyChange={(company) => {
            void navigate({ search: { ...search, company } });
          }}
          onSection={(section) => {
            void navigate({ to: financePaths[section] });
          }}
        />
      );
    },
  });
  const remittancesRoute = createRoute({
    getParentRoute: () => authenticatedRoute,
    path: '/finance/remittances',
    component: function RemittancesRoute() {
      const navigate = remittancesRoute.useNavigate();
      return (
        <RemittancesPage
          api={api}
          shipping={shipping}
          onOpen={(remittanceId) => {
            void navigate({ to: '/finance/remittances/$remittanceId', params: { remittanceId } });
          }}
          onSection={(section) => {
            void navigate({ to: financePaths[section] });
          }}
        />
      );
    },
  });
  const remittanceRoute = createRoute({
    getParentRoute: () => authenticatedRoute,
    path: '/finance/remittances/$remittanceId',
    component: function RemittanceRoute() {
      const { remittanceId } = remittanceRoute.useParams();
      const navigate = remittanceRoute.useNavigate();
      return (
        <RemittancePage
          api={api}
          remittanceId={remittanceId}
          onBack={() => {
            void navigate({ to: '/finance/remittances' });
          }}
        />
      );
    },
  });
  const returnsRoute = createRoute({
    getParentRoute: () => authenticatedRoute,
    path: '/returns',
    validateSearch: validateReturnsSearch,
    search: { middlewares: [stripSearchParams(RETURNS_DEFAULTS)] },
    component: function ReturnsRoute() {
      const filters: ReturnsSearch = returnsRoute.useSearch();
      const navigate = returnsRoute.useNavigate();
      return (
        <ReturnsPage
          api={api}
          shipping={shipping}
          filters={filters}
          onFiltersChange={(next) => {
            void navigate({ search: next, replace: true });
          }}
        />
      );
    },
  });
  const returnedParcelsRoute = createRoute({
    getParentRoute: () => authenticatedRoute,
    path: '/returned-parcels',
    validateSearch: validateReturnedParcelsSearch,
    search: { middlewares: [stripSearchParams(RETURNED_PARCELS_DEFAULTS)] },
    component: function ReturnedParcelsRoute() {
      const search: ReturnedParcelsSearch = returnedParcelsRoute.useSearch();
      const navigate = returnedParcelsRoute.useNavigate();
      return (
        <ReturnedParcelsPage
          api={api}
          shipping={shipping}
          filters={search}
          status={search.status}
          openOrderId={search.order}
          onFiltersChange={(filters) => {
            void navigate({ search: { ...search, ...filters, order: null }, replace: true });
          }}
          onStatusChange={(status) => {
            void navigate({ search: { ...search, status, order: null }, replace: true });
          }}
          onOpenOrder={(order) => {
            void navigate({ search: { ...search, order } });
          }}
          onEdit={(orderId) => {
            void navigate({ to: '/orders/$orderId/edit', params: { orderId } });
          }}
          onOpenSettings={() => {
            void navigate({ to: '/settings' });
          }}
          onReceive={() => {
            void navigate({ to: '/returns', search: RETURNS_DEFAULTS });
          }}
        />
      );
    },
  });
  const missingParcelsRoute = createRoute({
    getParentRoute: () => authenticatedRoute,
    path: '/missing-parcels',
    validateSearch: validateMissingParcelsSearch,
    search: { middlewares: [stripSearchParams(MISSING_PARCELS_DEFAULTS)] },
    component: function MissingParcelsRoute() {
      const search: MissingParcelsSearch = missingParcelsRoute.useSearch();
      const navigate = missingParcelsRoute.useNavigate();
      return (
        <MissingParcelsPage
          api={api}
          shipping={shipping}
          filters={search}
          openOrderId={search.order}
          onFiltersChange={(filters) => {
            void navigate({ search: { ...search, ...filters, order: null }, replace: true });
          }}
          onOpenOrder={(order) => {
            void navigate({ search: { ...search, order } });
          }}
          onEdit={(orderId) => {
            void navigate({ to: '/orders/$orderId/edit', params: { orderId } });
          }}
          onOpenSettings={() => {
            void navigate({ to: '/settings' });
          }}
        />
      );
    },
  });
  const handoverRoute = createRoute({
    getParentRoute: () => authenticatedRoute,
    path: '/handover',
    validateSearch: validateHandoverSearch,
    search: { middlewares: [stripSearchParams(HANDOVER_DEFAULTS)] },
    component: function HandoverRoute() {
      const search: HandoverSearch = handoverRoute.useSearch();
      const navigate = handoverRoute.useNavigate();
      return (
        <HandoverPage
          shipping={shipping}
          handoverId={search.handover}
          filters={search}
          onFiltersChange={(filters) => {
            void navigate({ search: { ...filters, handover: null }, replace: true });
          }}
          onOpenHandover={(handover) => {
            void navigate({ search: (previous) => ({ ...previous, handover }) });
          }}
        />
      );
    },
  });
  const customersRoute = createRoute({
    getParentRoute: () => authenticatedRoute,
    path: '/customers',
    validateSearch: validateCustomersSearch,
    search: { middlewares: [stripSearchParams(CUSTOMERS_DEFAULTS)] },
    component: function CustomersRoute() {
      const { customer, ...filters }: CustomersSearch = customersRoute.useSearch();
      const navigate = customersRoute.useNavigate();
      return (
        <CustomersPage
          api={api}
          filters={filters}
          openCustomerId={customer}
          onFiltersChange={(next) => {
            void navigate({ search: { ...next, customer } });
          }}
          onOpenCustomer={(customerId) => {
            void navigate({ search: { ...filters, customer: customerId } });
          }}
        />
      );
    },
  });
  const categoriesRoute = createRoute({
    getParentRoute: () => authenticatedRoute,
    path: '/categories',
    validateSearch: validateCategoriesSearch,
    search: { middlewares: [stripSearchParams(CATEGORIES_DEFAULTS)] },
    component: function CategoriesRoute() {
      const search: CategoriesSearch = categoriesRoute.useSearch();
      const navigate = categoriesRoute.useNavigate();
      return (
        <CategoriesPage
          api={api}
          status={search.status}
          onStatusChange={(status) => {
            void navigate({ search: { status } });
          }}
        />
      );
    },
  });
  const productsRoute = createRoute({
    getParentRoute: () => authenticatedRoute,
    path: '/products',
    validateSearch: validateProductsSearch,
    search: { middlewares: [stripSearchParams(PRODUCTS_DEFAULTS)] },
    component: function ProductsRoute() {
      const { product, created, ...filters }: ProductsSearch = productsRoute.useSearch();
      const navigate = productsRoute.useNavigate();
      const openLabels = useOpenLabels();
      return (
        <ProductsPage
          api={api}
          filters={filters}
          openProductId={product}
          createdProductId={created}
          onFiltersChange={(next) => {
            void navigate({ search: { ...next, product, created: null } });
          }}
          onOpenProduct={(productId) => {
            void navigate({ search: { ...filters, created, product: productId } });
          }}
          onCreate={() => {
            void navigate({ to: '/products/new' });
          }}
          onEdit={(productId) => {
            void navigate({ to: '/products/$productId', params: { productId } });
          }}
          onPrintLabels={(productIds) => {
            openLabels({ productIds, focus: null });
          }}
        />
      );
    },
  });
  const labelsRoute = createRoute({
    getParentRoute: () => authenticatedRoute,
    path: '/products/labels',
    validateSearch: validateLabelsSearch,
    search: { middlewares: [stripSearchParams(LABELS_DEFAULTS)] },
    component: function LabelsRoute() {
      const { product, variant, quantity }: LabelsSearch = labelsRoute.useSearch();
      const navigate = labelsRoute.useNavigate();
      return (
        <LabelsPage
          api={api}
          productIds={product}
          focus={variant === null || quantity === null ? null : { variantId: variant, quantity }}
          onBack={() => {
            void navigate({ to: '/products', search: PRODUCTS_DEFAULTS });
          }}
        />
      );
    },
  });
  const newProductRoute = createRoute({
    getParentRoute: () => authenticatedRoute,
    path: '/products/new',
    component: function NewProductRoute() {
      return <ProductEditorRoute api={api} productId={null} />;
    },
  });
  const editProductRoute = createRoute({
    getParentRoute: () => authenticatedRoute,
    path: '/products/$productId',
    component: function EditProductRoute() {
      const { productId } = editProductRoute.useParams();
      return <ProductEditorRoute api={api} productId={productId} />;
    },
  });
  const inventoryRoute = createRoute({
    getParentRoute: () => authenticatedRoute,
    path: '/inventory',
    validateSearch: validateInventorySearch,
    search: { middlewares: [stripSearchParams(INVENTORY_DEFAULTS)] },
    component: function InventoryRoute() {
      const search: InventorySearch = inventoryRoute.useSearch();
      const navigate = inventoryRoute.useNavigate();
      const openLabels = useOpenLabels();
      return (
        <InventoryPage
          api={api}
          tab={search.tab}
          state={search.state}
          depot={search.depot}
          onTabChange={(tab: InventoryTab) => {
            void navigate({ search: { ...search, tab } });
          }}
          onStateChange={(state) => {
            void navigate({ search: { ...search, state } });
          }}
          onDepotChange={(depot) => {
            void navigate({ search: { ...search, depot } });
          }}
          onShowDepotMovements={(depot) => {
            void navigate({ search: { ...search, tab: 'movements', state: null, depot } });
          }}
          onPrintLabels={openLabels}
        />
      );
    },
  });
  const statisticsOptions = {
    getParentRoute: () => authenticatedRoute,
    validateSearch: validateStatisticsSearch,
    search: { middlewares: [stripSearchParams<StatisticsSearch>(STATISTICS_DEFAULTS)] },
    beforeLoad: requireRole(STATISTICS_ROLE),
  };
  const statisticsRoutes = [
    createRoute({
      ...statisticsOptions,
      path: '/statistics/delivery',
      component: () => <StatisticsRoute api={api} shipping={shipping} tab="delivery" />,
    }),
    createRoute({
      ...statisticsOptions,
      path: '/statistics/confirmation',
      component: () => <StatisticsRoute api={api} shipping={shipping} tab="confirmation" />,
    }),
    createRoute({
      ...statisticsOptions,
      path: '/statistics/products',
      component: () => <StatisticsRoute api={api} shipping={shipping} tab="products" />,
    }),
    createRoute({
      ...statisticsOptions,
      path: '/statistics/orders',
      component: () => <StatisticsRoute api={api} shipping={shipping} tab="orders" />,
    }),
    createRoute({
      ...statisticsOptions,
      path: '/statistics/marketing',
      component: () => <StatisticsRoute api={api} shipping={shipping} tab="marketing" />,
    }),
  ] as const;
  const calculatorRoute = createRoute({
    getParentRoute: () => authenticatedRoute,
    path: '/calculator',
    component: function CalculatorRoute() {
      return <CalculatorPage api={api} />;
    },
  });
  const settingsRoute = createRoute({
    getParentRoute: () => authenticatedRoute,
    path: '/settings',
    component: function SettingsRoute() {
      const navigate = settingsRoute.useNavigate();
      return (
        <SettingsPage
          api={api}
          shipping={shipping}
          onOpenHistory={() => {
            void navigate({ to: '/settings/history', search: HISTORY_DEFAULTS });
          }}
          onOpenSetupStep={(step) => {
            void navigate({ to: '/setup', search: { step } });
          }}
        />
      );
    },
  });
  const setupRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/setup',
    validateSearch: validateSetupSearch,
    search: { middlewares: [stripSearchParams(SETUP_DEFAULTS)] },
    beforeLoad: (match) => {
      requireSignIn(match);
      requireRole(SETTINGS_MANAGE_ROLE)(match);
    },
    component: function SetupStepsRoute() {
      const search: SetupSearch = setupRoute.useSearch();
      return <SetupRoute api={api} shipping={shipping} step={search.step} />;
    },
  });
  const notificationsRoute = createRoute({
    getParentRoute: () => authenticatedRoute,
    path: '/notifications',
    validateSearch: validateNotificationsSearch,
    search: { middlewares: [stripSearchParams(NOTIFICATIONS_DEFAULTS)] },
    component: function NotificationsRoute() {
      const { filter }: NotificationsSearch = notificationsRoute.useSearch();
      const navigate = notificationsRoute.useNavigate();
      return (
        <NotificationsPage
          api={api}
          filter={filter}
          onFilterChange={(next) => {
            void navigate({ search: { filter: next } });
          }}
        />
      );
    },
  });

  const historyRoute = createRoute({
    getParentRoute: () => authenticatedRoute,
    path: '/settings/history',
    validateSearch: validateHistorySearch,
    search: { middlewares: [stripSearchParams(HISTORY_DEFAULTS)] },
    beforeLoad: requireRole(AUDIT_READ_ROLE),
    component: function HistoryRoute() {
      const filters: HistorySearch = historyRoute.useSearch();
      const navigate = historyRoute.useNavigate();
      const openSubject = useOpenHistorySubject();
      return (
        <HistoryPage
          api={api}
          filters={filters}
          onFiltersChange={(next) => {
            void navigate({ search: next });
          }}
          onOpenSubject={openSubject}
          onBack={() => {
            void navigate({ to: '/settings' });
          }}
        />
      );
    },
  });
  const tenantsRoute = createRoute({
    getParentRoute: () => authenticatedRoute,
    path: '/tenants',
    beforeLoad: requireRole(SUPER_ADMIN_ROLE),
    component: () => <PlaceholderPage titleKey="nav.tenants" />,
  });

  return rootRoute.addChildren([
    loginRoute,
    callbackRoute,
    forbiddenRoute,
    setupRoute,
    authenticatedRoute.addChildren([
      dashboardRoute,
      ordersRoute,
      newOrderRoute,
      editOrderRoute,
      handoverRoute,
      returnsRoute,
      returnedParcelsRoute,
      missingParcelsRoute,
      financeRoute,
      disputesRoute,
      expensesRoute,
      remittancesRoute,
      remittanceRoute,
      customersRoute,
      productsRoute,
      labelsRoute,
      newProductRoute,
      editProductRoute,
      categoriesRoute,
      inventoryRoute,
      ...statisticsRoutes,
      calculatorRoute,
      settingsRoute,
      historyRoute,
      notificationsRoute,
      tenantsRoute,
    ]),
  ]);
};

export type RouterLocation = { readonly history: RouterHistory; readonly currentLocale: () => Locale };

export const createAppRouter = (clients: ApiClients, context: RouterContext, location: RouterLocation) =>
  createRouter({
    routeTree: buildRouteTree(clients),
    context,
    history: location.history,
    rewrite: localeRewrite(location.currentLocale),
    defaultPreload: 'intent',
  });

export type AppRouter = ReturnType<typeof createAppRouter>;
